import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { NEUTRAL_INPUT, type VehicleInput } from '../model/vehicleDriving';
import { mergeDrivingInputs, readGamepadInput } from '../model/gamepadInput';

/** 조작 하나. 키보드와 화면 버튼이 같은 이름을 씁니다. */
export type DrivingAction = 'throttle' | 'reverse' | 'left' | 'right' | 'brake';

export type DrivingActionState = Record<DrivingAction, boolean>;

const IDLE_ACTIONS: DrivingActionState = {
  throttle: false,
  reverse: false,
  left: false,
  right: false,
  brake: false,
};

/**
 * 키 → 조작 매핑. 방향키와 WASD를 모두 받습니다.
 *
 * 대문자를 함께 넣는 이유는 Shift를 누른 채로도 키가 먹어야 하기 때문입니다
 * (event.key는 Shift+w에서 'W'를 줍니다). CapsLock도 같은 경로로 들어옵니다.
 */
const KEY_ACTIONS: Record<string, DrivingAction> = {
  ArrowUp: 'throttle',
  w: 'throttle',
  W: 'throttle',
  ArrowDown: 'reverse',
  s: 'reverse',
  S: 'reverse',
  ArrowLeft: 'left',
  a: 'left',
  A: 'left',
  ArrowRight: 'right',
  d: 'right',
  D: 'right',
  ' ': 'brake',
};

/**
 * 조작 상태를 운동 모델의 입력으로 바꿉니다.
 *
 * 양쪽을 동시에 누르면 상쇄됩니다(0). 한쪽을 고르는 대신 상쇄시키는 이유는, 방향키를
 * 번갈아 누를 때 우선순위 규칙이 있으면 손을 뗀 순서에 따라 차가 예상과 다르게 꺾입니다.
 *
 * steer의 부호는 heading과 같습니다(양수가 좌회전). vehicleDriving에서 부호를 뒤집지
 * 않으려고 여기서 맞춰 둡니다.
 */
export function toVehicleInput(actions: DrivingActionState): VehicleInput {
  return {
    throttle: (actions.throttle ? 1 : 0) - (actions.reverse ? 1 : 0),
    steer: (actions.left ? 1 : 0) - (actions.right ? 1 : 0),
    // 키보드·터치는 아날로그가 없으므로 0 또는 1만 보냅니다.
    brake: actions.brake ? 1 : 0,
  };
}

export interface DrivingInputController {
  /** 주행 루프가 매 프레임 읽습니다. */
  inputRef: RefObject<VehicleInput>;
  /** 화면 버튼의 눌림 표시용. 프레임마다 바뀌지 않으므로 상태로 둡니다. */
  actions: DrivingActionState;
  press: (action: DrivingAction) => void;
  release: (action: DrivingAction) => void;
  releaseAll: () => void;
  /** 연결된 게임패드 이름. 없으면 null입니다(저빈도라 상태로 둡니다). */
  gamepadName: string | null;
  /** 진동을 보낼 패드 인덱스. 훅 사용자가 haptics에 넘깁니다. */
  gamepadIndexRef: RefObject<number | null>;
}

/**
 * 주행 조작 입력. 키보드를 직접 듣고, 화면 버튼은 press/release를 호출합니다.
 *
 * @param enabled 주행 모드일 때만 true. false면 키보드를 듣지 않습니다 — 보기 모드에서
 *   방향키를 삼키면 패널을 키보드로 훑을 수 없게 됩니다.
 */
export function useDrivingInput(enabled: boolean): DrivingInputController {
  const [actions, setActions] = useState<DrivingActionState>(IDLE_ACTIONS);
  const [gamepadName, setGamepadName] = useState<string | null>(null);
  const inputRef = useRef<VehicleInput>(NEUTRAL_INPUT);
  const digitalInputRef = useRef<VehicleInput>(NEUTRAL_INPUT);
  const gamepadIndexRef = useRef<number | null>(null);

  /*
   * 입력 계산은 렌더 중에 해 두고 ref에는 커밋 후에 씁니다. effect로 미루면 눌린
   * 프레임과 반영되는 프레임이 갈리는데, 그 차이가 조작 지연으로 느껴집니다.
   */
  const input = useMemo(() => toVehicleInput(actions), [actions]);
  useEffect(() => {
    digitalInputRef.current = input;
    /*
     * 패드 폴링 루프가 매 프레임 같은 ref를 덮어쓰지만, 패드가 없으면 그 루프가
     * 돌지 않으므로 여기서도 반영해 둡니다.
     */
    if (gamepadIndexRef.current === null) {
      inputRef.current = input;
    }
  }, [input]);

  /**
   * 게임패드 폴링.
   *
   * Gamepad API는 이벤트로 축 값을 주지 않아서 폴링해야 합니다. 축 값을 React state로
   * 올리면 프레임마다 트리가 다시 렌더되므로, 여기서는 ref만 갱신하고 화면에 보이는
   * 것(연결된 패드 이름)만 저빈도 state로 둡니다.
   */
  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || navigator.getGamepads === undefined) {
      gamepadIndexRef.current = null;
      setGamepadName(null);
      return undefined;
    }

    let frame = 0;
    let shownName: string | null = null;

    const tick = () => {
      const pads = navigator.getGamepads();
      let active: Gamepad | null = null;
      for (const pad of pads) {
        if (pad !== null && pad.connected) {
          active = pad;
          break;
        }
      }

      gamepadIndexRef.current = active === null ? null : active.index;
      const analog = readGamepadInput(active);
      inputRef.current = mergeDrivingInputs(digitalInputRef.current, analog);

      const name = active === null ? null : active.id;
      if (name !== shownName) {
        shownName = name;
        setGamepadName(name);
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      gamepadIndexRef.current = null;
      inputRef.current = digitalInputRef.current;
      setGamepadName(null);
    };
  }, [enabled]);

  const press = useCallback((action: DrivingAction) => {
    setActions((current) => (current[action] ? current : { ...current, [action]: true }));
  }, []);

  const release = useCallback((action: DrivingAction) => {
    setActions((current) => (current[action] ? { ...current, [action]: false } : current));
  }, []);

  const releaseAll = useCallback(() => {
    setActions((current) => (current === IDLE_ACTIONS ? current : IDLE_ACTIONS));
  }, []);

  useEffect(() => {
    if (!enabled) {
      // 모드를 나갈 때 누르고 있던 키가 남으면 다시 들어올 때 차가 저절로 출발합니다.
      releaseAll();
      return undefined;
    }

    const resolve = (event: KeyboardEvent): DrivingAction | undefined => {
      // 조합키가 붙은 것은 브라우저·OS 단축키입니다. 가로채면 앱을 벗어날 방법을 잃습니다.
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return undefined;
      }
      return KEY_ACTIONS[event.key];
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const action = resolve(event);
      if (action === undefined) {
        return;
      }
      // 방향키는 페이지를 스크롤하고 스페이스는 버튼을 누릅니다. 주행 중에는 조작입니다.
      event.preventDefault();
      press(action);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      const action = resolve(event);
      if (action !== undefined) {
        release(action);
      }
    };

    /*
     * 키를 누른 채 탭을 벗어나면 keyup을 못 받습니다. 그대로 두면 돌아왔을 때 차가
     * 저절로 달립니다. visibilitychange도 함께 듣는 이유는, 모바일에서 앱을 백그라운드로
     * 보낼 때 blur가 오지 않는 경우가 있기 때문입니다.
     */
    const handleLeave = () => releaseAll();

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleLeave);
    document.addEventListener('visibilitychange', handleLeave);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleLeave);
      document.removeEventListener('visibilitychange', handleLeave);
      releaseAll();
    };
  }, [enabled, press, release, releaseAll]);

  return { inputRef, actions, press, release, releaseAll, gamepadName, gamepadIndexRef };
}
